import React from "react";
import * as yup from "yup";
import { useFormik } from "formik";
import { Checkbox, FormControlLabel, FormControl, InputLabel, MenuItem, Select, TextField, FormGroup } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import Div from "@jumbo/shared/Div";
import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog';
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import subTaskService from 'app/services/subtask_service';

const getCurrentDate = () => new Date().toISOString().split('T')[0];

const FinancePaymentStatusForm = ({ expenseIds, hrFinanceStatus }) => {
    const Swal = useSwalWrapper();
    const { hideDialog } = useJumboDialog();

    const sweetAlerts = (variant) => {
        Swal.fire({
            icon: variant,
            title: variant === "success" ? 'Success' : 'Error',
            text: variant === "success" ? 'Data entered successfully!' : "Something went wrong.",
        }).then(() => {
            window.location.reload();
        });
    };

    const validationSchema = yup.object({
        paymentStatus: yup.boolean().required('Payment status is required.'),
        paymentDate: yup.date().required('Payment date is required.'),
        paidBy: yup.string().required('Paid by is required.'),
    });


    const initialValues = {
        paymentStatus: false,
        paymentDate: '',
        paidBy: '',
        status: ''
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        validateOnChange: true,
        onSubmit: (values, { setSubmitting }) => {
            const payload = {
                expenseIds,
                payment_status: values.paymentStatus,
                payment_date: values.paymentDate,
                paid_by: values.paidBy,
                status: hrFinanceStatus
            };
            subTaskService.updatePaymentStatus(payload)
                .then(() => {
                    hideDialog();
                    sweetAlerts('success');
                })
                .catch(() => {
                    hideDialog();
                    sweetAlerts('error');
                })
                .finally(() => {
                    setSubmitting(false);
                });
        },
    });

    return (
        <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
            <Div sx={{ '& .MuiTextField-root': { mb: 3 } }}>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formik.values.paymentStatus}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    formik.setFieldValue("paymentStatus", isChecked);
                                    // Reset dependent fields if checkbox is unchecked
                                    if (!isChecked) {
                                        formik.setFieldValue("paymentDate", "");
                                        formik.setFieldValue("paidBy", "");
                                    }
                                }}
                                name="paymentStatus"
                            />
                        }
                        label="Paid"
                    />
                </FormGroup>

                <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel id="paid-from-label">Paid From</InputLabel>
                    <Select
                        labelId="paid-from-label"
                        id="paid-from-select"
                        name="paidBy"
                        value={formik.values.paidBy}
                        onChange={formik.handleChange}
                        label="Paid From"
                        error={formik.touched.paidBy && Boolean(formik.errors.paidBy)}
                        disabled={!formik.values.paymentStatus}
                    >
                        <MenuItem value="Blackmilk Media Pvt Ltd -current a/c">Blackmilk Media Pvt Ltd -current a/c</MenuItem>
                        <MenuItem value="Pianosa Pvt Ltd - current a/c">Pianosa Pvt Ltd - current a/c</MenuItem>
                        <MenuItem value="Credit Card - 001">Credit Card - 001</MenuItem>
                        <MenuItem value="Credit Card - 007">Credit Card - 007</MenuItem>
                        <MenuItem value="Credit Card - 6003">Credit Card - 6003</MenuItem>
                        <MenuItem value="Credit Card - 6005">Credit Card - 6005</MenuItem>
                    </Select>
                    {formik.touched.paidBy && formik.errors.paidBy && <p style={{ color: 'red', fontSize: '0.75rem', marginTop: '4px', marginLeft: '14px' }}>{formik.errors.paidBy}</p>}
                </FormControl>

                <TextField
                    fullWidth
                    id="paymentDate"
                    name="paymentDate"
                    label="Payment Date"
                    type="date"
                    sx={{ mt: 3 }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: getCurrentDate() }}
                    value={formik.values.paymentDate}
                    onChange={formik.handleChange}
                    error={formik.touched.paymentDate && Boolean(formik.errors.paymentDate)}
                    helperText={formik.touched.paymentDate && formik.errors.paymentDate}
                    disabled={!formik.values.paymentStatus}
                />

                <LoadingButton
                    fullWidth
                    type="submit"
                    variant="contained"
                    size="large"
                    sx={{ mt: 3, mb: 3 }}
                    loading={formik.isSubmitting}
                >
                    Save
                </LoadingButton>
            </Div>
        </form>
    );
};

export default FinancePaymentStatusForm;
